import React, { useEffect, useMemo, useState } from "react";
import "./Venues.css";
import { CircularProgress, Grid } from "@mui/material";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { getVenues } from "../../utils/clientAccessApi";

const DEFAULT_RADIUS_KM = 30;

const normalizeResponseBadge = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

const isEmptyResponseBadge = (value) => {
  const normalized = normalizeResponseBadge(value);

  if (!normalized) {
    return true;
  }

  return (
    normalized.includes("no response") ||
    normalized === "без податок" ||
    normalized === "нема податок" ||
    normalized === "n/a"
  );
};

const categories = [
  { id: "332c6a1c-fc3d-4a04-90a1-326014f81bc1", name: "Облека" },
  { id: "c9163180-8567-4c5a-b76a-6abc0b9f73ec", name: "Музика" },
  { id: "fcbca939-d505-489f-9b5c-630ea1f3e430", name: "Декорации" },
  { id: "647d5787-4fb6-499a-b5a1-4356b438c89a", name: "Фотографи" },
  { id: "0b7588f1-3e5c-47a5-9e7e-5e23f7cbf6a8", name: "Козметика" },
  { id: "d3f092e6-b1fa-4c89-b6ad-82cde08f9be1", name: "Ресторани" },
  { id: "15a858ea-2d38-4deb-9578-4139d3db6187", name: "Кетеринг/Торти" },
  { id: "5b52bd52-11ea-4b54-a964-6539368b4989", name: "Хотели" },
  { id: "2cbadf3a-6f10-40d8-b2bc-e45c838c6154", name: "Останато" },
];

const initialFilters = {
  city: "",
  service_type: "",
  style: "",
  language: "",
  guest_count: "",
  budget_min: "",
  budget_max: "",
  date: "",
  sort_by: "newest",
  order: "desc",
  available_only: false,
  radius_km: String(DEFAULT_RADIUS_KM),
};

const revealContainer = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.06,
      delayChildren: 0.06,
    },
  },
};

const revealItem = {
  hidden: { opacity: 0, y: 18 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.28, ease: "easeOut" },
  },
};

const formatPrice = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return null;
  }

  return Math.trunc(parsed).toLocaleString("mk-MK");
};

const formatPriceRange = (venue) => {
  const from = formatPrice(venue?.price_from);
  const to = formatPrice(venue?.price_to);

  if (from && to) {
    return `${from} - ${to} ден`;
  }

  if (from) {
    return `Од ${from} ден`;
  }

  if (to) {
    return `До ${to} ден`;
  }

  return "Цена по договор";
};

const getVenueBlurb = (venue) => {
  const rawText = String(venue?.details || "").replace(/\s+/g, " ").trim();
  if (!rawText) {
    return "Погледнете повеќе детали за услугите, стилот и контакт опциите.";
  }

  if (rawText.length <= 120) {
    return rawText;
  }

  return `${rawText.slice(0, 117).trim()}...`;
};

const getResponseBadgeLabel = (venue) => {
  const badge = String(venue?.response_time_badge || "").trim();
  if (isEmptyResponseBadge(badge)) {
    return "Во брзо време";
  }

  return badge;
};

function Venues() {
  const [searchParams] = useSearchParams();
  const requestedCategory = searchParams.get("category_id");
  const initialCategory = categories.some((category) => category.id === requestedCategory) ? requestedCategory : categories[0].id;
  const [activeCategory, setActiveCategory] = useState(initialCategory);
  useEffect(() => { setActiveCategory(initialCategory); }, [initialCategory]);
  const [filters, setFilters] = useState(initialFilters);
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState("");
  const [locationState, setLocationState] = useState({
    enabled: false,
    lat: "",
    lng: "",
    busy: false,
    error: "",
  });

  useEffect(() => {
    let isMounted = true;

    const loadVenues = async () => {
      try {
        setIsFetching(true);
        setError("");

        const data = await getVenues({
          category_id: activeCategory,
          city: filters.city.trim(),
          service_type: filters.service_type.trim(),
          style: filters.style.trim(),
          language: filters.language.trim(),
          guest_count: filters.guest_count,
          budget_min: filters.budget_min,
          budget_max: filters.budget_max,
          date: filters.date,
          sort_by: filters.sort_by,
          order: filters.order,
          available_only: filters.available_only ? "true" : "",
          lat: locationState.enabled ? locationState.lat : "",
          lng: locationState.enabled ? locationState.lng : "",
          radius_km: locationState.enabled ? filters.radius_km : "",
        });

        if (!isMounted) {
          return;
        }

        setVenues(Array.isArray(data) ? data : []);
      } catch (fetchError) {
        if (!isMounted) {
          return;
        }

        console.error("Error fetching venues:", fetchError);
        setError(
          fetchError.message || "Не успеавме да ги вчитаме понудувачите."
        );
        setVenues([]);
      } finally {
        if (isMounted) {
          setLoading(false);
          setIsFetching(false);
        }
      }
    };

    loadVenues();

    return () => {
      isMounted = false;
    };
  }, [activeCategory, filters, locationState.enabled, locationState.lat, locationState.lng]);

  const activeCategoryName = useMemo(() => {
    return categories.find((category) => category.id === activeCategory)?.name;
  }, [activeCategory]);

  const updateFilter = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const resetFilters = () => {
    setFilters(initialFilters);
    setLocationState((current) => ({
      ...current,
      enabled: false,
      error: "",
    }));
  };

  const handleUseMyLocation = () => {
    if (locationState.enabled) {
      setLocationState((current) => ({
        ...current,
        enabled: false,
        error: "",
      }));
      return;
    }

    if (!navigator.geolocation) {
      setLocationState((current) => ({
        ...current,
        error: "Вашиот прелистувач не поддржува геолокација.",
      }));
      return;
    }

    setLocationState((current) => ({
      ...current,
      busy: true,
      error: "",
    }));

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocationState({
          enabled: true,
          busy: false,
          error: "",
          lat: String(position.coords.latitude),
          lng: String(position.coords.longitude),
        });
      },
      () => {
        setLocationState((current) => ({
          ...current,
          busy: false,
          error: "Не успеавме да ја прочитаме вашата локација.",
        }));
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  return (
    <div className="venues-container venues-index-page">
      <div className="venues-index-header">
        <span className="venues-index-eyebrow">ОТКРИЈТЕ ПОНУДУВАЧИ</span>
        <h1 className="venues-index-title">Пронајдете го вистинскиот тим</h1>
        <p className="venues-index-subtitle">
          Филтрирајте по град, услуга, стил, буџет и достапност за побрзо да
          стигнете до вистински избор.
        </p>
      </div>

      <div className="tabs-container">
        {categories.map((category) => (
          <button
            key={category.id}
            className={`tab-button ${
              activeCategory === category.id ? "active" : ""
            }`}
            onClick={() => setActiveCategory(category.id)}
          >
            {category.name}
          </button>
        ))}
      </div>

      <motion.section
        className="venues-filters-panel"
        initial="hidden"
        animate="show"
        variants={revealItem}
      >
        <div className="venues-filters-topline">
          <div>
            <p className="venues-filters-label">Категорија</p>
            <strong>{activeCategoryName}</strong>
          </div>
          <div className="venues-filters-actions">
            <button
              type="button"
              className={`venues-location-button ${
                locationState.enabled ? "is-active" : ""
              }`}
              onClick={handleUseMyLocation}
              disabled={locationState.busy}
            >
              {locationState.busy
                ? "Се вчитува локација..."
                : locationState.enabled
                  ? "Локација е активна"
                  : "Во близина на мене"}
            </button>
            <button
              type="button"
              className="venues-reset-button"
              onClick={resetFilters}
            >
              Исчисти филтри
            </button>
          </div>
        </div>

        <div className="venues-filters-grid">
          <label className="venues-filter-field">
            <span>Град</span>
            <input
              type="text"
              value={filters.city}
              onChange={(event) => updateFilter("city", event.target.value)}
              placeholder="Пр. Скопје"
            />
          </label>

          <label className="venues-filter-field">
            <span>Тип на услуга</span>
            <input
              type="text"
              value={filters.service_type}
              onChange={(event) =>
                updateFilter("service_type", event.target.value)
              }
              placeholder="Пр. wedding venue"
            />
          </label>

          <label className="venues-filter-field">
            <span>Стил</span>
            <input
              type="text"
              value={filters.style}
              onChange={(event) => updateFilter("style", event.target.value)}
              placeholder="Пр. modern"
            />
          </label>

          <label className="venues-filter-field">
            <span>Јазик</span>
            <input
              type="text"
              value={filters.language}
              onChange={(event) => updateFilter("language", event.target.value)}
              placeholder="Пр. македонски"
            />
          </label>

          <label className="venues-filter-field">
            <span>Број гости</span>
            <input
              type="number"
              min="1"
              value={filters.guest_count}
              onChange={(event) =>
                updateFilter("guest_count", event.target.value)
              }
              placeholder="150"
            />
          </label>

          <label className="venues-filter-field">
            <span>Буџет од</span>
            <input
              type="number"
              min="0"
              value={filters.budget_min}
              onChange={(event) => updateFilter("budget_min", event.target.value)}
              placeholder="1000"
            />
          </label>

          <label className="venues-filter-field">
            <span>Буџет до</span>
            <input
              type="number"
              min="0"
              value={filters.budget_max}
              onChange={(event) => updateFilter("budget_max", event.target.value)}
              placeholder="3000"
            />
          </label>

          <label className="venues-filter-field">
            <span>Датум</span>
            <input
              type="date"
              value={filters.date}
              onChange={(event) => updateFilter("date", event.target.value)}
            />
          </label>

          <label className="venues-filter-field">
            <span>Сортирај</span>
            <select
              value={`${filters.sort_by}:${filters.order}`}
              onChange={(event) => {
                const [sortBy, order] = event.target.value.split(":");
                setFilters((current) => ({
                  ...current,
                  sort_by: sortBy,
                  order,
                }));
              }}
            >
              <option value="newest:desc">Најново</option>
              <option value="best_value:desc">Најдобра вредност</option>
              <option value="response_time:desc">Најбрз одговор</option>
              <option value="most_viewed:desc">Најгледани</option>
              <option value="name:asc">Име А-Ш</option>
              <option value="city:asc">Град А-Ш</option>
            </select>
          </label>

          {locationState.enabled ? (
            <label className="venues-filter-field">
              <span>Радиус</span>
              <select
                value={filters.radius_km}
                onChange={(event) => updateFilter("radius_km", event.target.value)}
              >
                <option value="10">10 km</option>
                <option value="30">30 km</option>
                <option value="50">50 km</option>
                <option value="100">100 km</option>
              </select>
            </label>
          ) : null}

          <label className="venues-filter-toggle">
            <input
              type="checkbox"
              checked={filters.available_only}
              onChange={(event) =>
                updateFilter("available_only", event.target.checked)
              }
            />
            <span>Само веројатно достапни за избраниот датум</span>
          </label>
        </div>

        {locationState.error ? (
          <p className="venues-filter-note is-error">{locationState.error}</p>
        ) : null}
        {filters.date ? (
          <p className="venues-filter-note">
            За датумот {filters.date} backend-от враќа `likely_available` и
            резултатите можат да се ограничат само на достапни.
          </p>
        ) : null}
      </motion.section>

      <div className="venues-results-bar">
        <div>
          <strong>{venues.length}</strong> резултати
        </div>
        {isFetching ? (
          <div className="venues-results-loading">
            <CircularProgress size={18} />
            <span>Освежување...</span>
          </div>
        ) : null}
      </div>

      {error ? <div className="venues-inline-error">{error}</div> : null}

      <Grid
        component={motion.div}
        initial="hidden"
        animate="show"
        variants={revealContainer}
        container
        className="venues-list"
        spacing={2}
        direction={{ xs: "column", sm: "row" }}
        justifyContent={{ xs: "center", md: "flex-start" }}
        alignItems="flex-start"
        sx={{
          flexWrap: "wrap",
          px: { xs: 2, sm: 4 },
        }}
      >
        {loading ? (
          <Grid item size={{ xs: 12 }}>
            <div className="venues-loading-state">
              <CircularProgress size={28} />
              <span>Ги вчитуваме понудувачите...</span>
            </div>
          </Grid>
        ) : venues.length > 0 ? (
          venues.map((venue) => (
            <Grid item size={{ xs: 12, sm: 6, lg: 4 }} key={venue.id}>
              <Link to={`/venue/${venue.id}`} className="venue-card-link">
                <motion.div
                  className="venue-card venue-discovery-card"
                  variants={revealItem}
                  whileHover={{ y: -6 }}
                  transition={{ duration: 0.22, ease: "easeOut" }}
                >
                  <img
                    src={
                      venue.image_url ||
                      "https://placehold.co/600x400/weddings/white"
                    }
                    alt={venue.name}
                    className="venue-page-image"
                  />
                  <div className="venue-page-info">
                    <div className="venue-page-title-row">
                      <h3>{venue.name}</h3>
                      <span>{venue.city || "Локација по договор"}</span>
                    </div>

                    <div className="venue-card-badges">
                      {getResponseBadgeLabel(venue) ? (
                        <span className="venue-card-badge">
                          {getResponseBadgeLabel(venue)}
                        </span>
                      ) : null}
                      {filters.date && typeof venue.likely_available === "boolean" ? (
                        <span
                          className={`venue-card-badge ${
                            venue.likely_available
                              ? "is-success"
                              : "is-warning"
                          }`}
                        >
                          {venue.likely_available
                            ? "Веројатно достапно"
                            : "Веројатно зафатено"}
                        </span>
                      ) : null}
                    </div>

                    <p className="venue-card-price">{formatPriceRange(venue)}</p>
                    <p className="venue-card-copy">{getVenueBlurb(venue)}</p>

                    <div className="venue-card-stats">
                      <span>
                        {venue.rating_average
                          ? `${venue.rating_average}/5`
                          : "Нема оцени"}
                      </span>
                      <span>
                        {venue.reviews_count || 0} {venue.reviews_count === 1 ? "рецензија" : "рецензии"}
                      </span>
                      {venue.distance_km ? (
                        <span>{venue.distance_km} km</span>
                      ) : null}
                    </div>

                    {Array.isArray(venue.service_types) &&
                    venue.service_types.length > 0 ? (
                      <div className="venue-card-tags">
                        {venue.service_types.slice(0, 3).map((serviceType) => (
                          <span key={serviceType}>{serviceType}</span>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </motion.div>
              </Link>
            </Grid>
          ))
        ) : (
          <Grid item size={{ xs: 12 }}>
            <div className="no-venues-message">
              Нема резултати за избраните филтри. Променете град, буџет или
              датум и пробајте повторно.
            </div>
          </Grid>
        )}
      </Grid>
    </div>
  );
}

export default Venues;
